import { MapPinIcon, GlobeAltIcon } from "@heroicons/react/24/outline";
import {
  useDashboardInventory,
  type InventoryBreakdownRow,
} from "@/api/dashboard";
import { BrandedLoader } from "@/components/shared/BrandedLoader";
import { titleCase } from "@/utils/format";

// Four on-market segments. For-sale total = secondary + developer-primary +
// off-plan; rental is shown alongside for comparison (not part of the total).
const SEGMENTS = [
  { key: "secondary", label: "Secondary", color: "#0d9488" }, // teal-600
  { key: "developer_primary", label: "Ready-to-move (developer)", color: "#7c3aed" }, // violet-600
  { key: "off_plan", label: "Off-plan", color: "#f59e0b" }, // amber-500
  { key: "rental", label: "Rental", color: "#0ea5e9" }, // sky-500
] as const;

function Legend() {
  return (
    <div className="mb-3 flex flex-wrap gap-x-4 gap-y-1">
      {SEGMENTS.map((s) => (
        <span key={s.key} className="flex items-center gap-1.5 text-[11px] text-gray-500">
          <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: s.color }} />
          {s.label}
        </span>
      ))}
    </div>
  );
}

function BreakdownChart({
  rows,
  labelWidth,
}: {
  rows: InventoryBreakdownRow[];
  labelWidth: string;
}) {
  // Shared scale so every bar (main total + each sub-bar) is comparable across
  // rows and categories.
  const maxVal = Math.max(
    1,
    ...rows.flatMap((r) => [
      r.for_sale_total,
      r.secondary,
      r.developer_primary,
      r.off_plan,
      r.rental,
    ]),
  );
  const w = (v: number) => `${Math.max((v / maxVal) * 100, v > 0 ? 1.5 : 0)}%`;

  return (
    <div className="space-y-3">
      {rows.map((r) => (
        <div key={r.label} className="flex items-start gap-3">
          <span
            className={`${labelWidth} shrink-0 pt-0.5`}
            title={titleCase(r.label)}
          >
            <span className="block truncate text-xs font-medium text-gray-600">
              {titleCase(r.label)}
            </span>
            {(r.district || r.emirate) && (
              <span className="block truncate text-[10px] text-gray-400">
                (
                {[r.district, r.emirate]
                  .filter(Boolean)
                  .map((v) => titleCase(v as string))
                  .join(" · ")}
                )
              </span>
            )}
          </span>
          <div className="min-w-0 flex-1 space-y-1">
            {/* Main bar — total for sale */}
            <div className="flex items-center gap-2">
              <div className="h-3 flex-1 rounded bg-gray-100">
                <div
                  className="h-3 rounded bg-gray-500"
                  style={{ width: w(r.for_sale_total) }}
                />
              </div>
              <span className="w-14 shrink-0 text-right text-xs font-semibold tabular-nums text-gray-700">
                {r.for_sale_total.toLocaleString()}
              </span>
            </div>
            {/* Four sub-bars */}
            {SEGMENTS.map((s) => {
              const v = r[s.key] as number;
              return (
                <div key={s.key} className="flex items-center gap-2">
                  <div className="h-1.5 flex-1 rounded-full bg-gray-50">
                    <div
                      className="h-1.5 rounded-full"
                      style={{ width: w(v), backgroundColor: s.color }}
                    />
                  </div>
                  <span className="w-14 shrink-0 text-right text-[10px] tabular-nums text-gray-400">
                    {v.toLocaleString()}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export function InventorySummary() {
  const { data, isLoading } = useDashboardInventory();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <BrandedLoader size={40} label={null} />
      </div>
    );
  }

  if (!data) return null;

  const { properties: p } = data;
  const typeRows = p.by_type_breakdown ?? [];
  const communityRows = p.by_community_breakdown ?? [];

  return (
    // `items-stretch` so both cards take the height of the taller one, which
    // is Property Types — the fixed list the operator wants used as the guide
    // (MPPI-00654). Top Communities then scrolls inside that height rather
    // than stretching the row.
    <div className="grid grid-cols-1 items-stretch gap-3 lg:grid-cols-2">
      {/* Property types */}
      <div className="flex flex-col rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <h3 className="mb-1 flex items-center gap-2 text-sm font-semibold text-gray-700">
          <GlobeAltIcon className="h-4 w-4 text-indigo-500" />
          Property Types
          <span className="ml-1 text-[11px] font-normal text-gray-400">
            on-market, by for-sale total
          </span>
        </h3>
        <Legend />
        {typeRows.length > 0 ? (
          <BreakdownChart rows={typeRows} labelWidth="w-24" />
        ) : (
          <p className="py-6 text-center text-xs text-gray-400">No inventory data.</p>
        )}
      </div>

      {/* Top communities */}
      <div className="flex min-h-0 flex-col rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <h3 className="mb-1 flex items-center gap-2 text-sm font-semibold text-gray-700">
          <MapPinIcon className="h-4 w-4 text-teal-500" />
          Top Communities
          <span className="ml-1 text-[11px] font-normal text-gray-400">
            on-market, by for-sale total
          </span>
        </h3>
        <Legend />
        {communityRows.length > 0 ? (
          // Scrolls to the rest of the top 10 inside the shared height.
          <div className="min-h-0 flex-1 overflow-y-auto pr-1">
            <BreakdownChart rows={communityRows} labelWidth="w-28" />
          </div>
        ) : (
          <p className="py-6 text-center text-xs text-gray-400">No community data.</p>
        )}
      </div>
    </div>
  );
}
